import React, { useEffect, useState } from 'react';
import { Button, Spinner } from '../common';
import { PodcastFeed } from '../../types';
import { podcastFeedsApi } from '../../api/podcastFeeds';
import { trackEvent } from '../../lib/analytics';

interface PodcastFeedDetailProps {
  feedId: string;
  /** Called whenever the feed metadata changes so the parent list can refresh. */
  onChange?: () => void;
}

function formatDuration(seconds: number | null): string {
  if (!seconds) return '—';
  const mins = Math.max(0, Math.round(seconds / 60));
  return `${mins} min`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export default function PodcastFeedDetail({
  feedId,
  onChange,
}: PodcastFeedDetailProps) {
  const [feed, setFeed] = useState<PodcastFeed | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [copied, setCopied] = useState(false);

  const reload = async () => {
    setLoading(true);
    setError(null);
    try {
      const fresh = await podcastFeedsApi.get(feedId);
      setFeed(fresh);
      setName(fresh.name);
      setDescription(fresh.description ?? '');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load feed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedId]);

  const update = async (updater: () => Promise<PodcastFeed>) => {
    setSaving(true);
    setError(null);
    try {
      const fresh = await updater();
      setFeed(fresh);
      onChange?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const saveDetails = () =>
    update(() =>
      podcastFeedsApi.update(feedId, {
        name: name.trim(),
        description: description.trim() ? description.trim() : null,
      })
    );

  const togglePublished = () => {
    if (!feed) return;
    void update(() =>
      podcastFeedsApi.update(feedId, { published: !feed.published })
    );
  };

  const moveItem = (index: number, direction: -1 | 1) => {
    if (!feed) return;
    const target = index + direction;
    if (target < 0 || target >= feed.items.length) return;

    const ordered = feed.items.map((item) => item.conversionId);
    [ordered[index], ordered[target]] = [ordered[target], ordered[index]];
    void update(() => podcastFeedsApi.reorder(feedId, ordered));
  };

  const removeItem = (conversionId: string) => {
    void update(() => podcastFeedsApi.removeItem(feedId, conversionId));
  };

  const handleCopyUrl = async () => {
    if (!feed?.feedUrl) return;
    try {
      await navigator.clipboard.writeText(feed.feedUrl);
      trackEvent('feed_url_copied', { surface: 'curated_feed' });
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (loading || !feed) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner />
      </div>
    );
  }

  const dirty =
    name.trim() !== feed.name ||
    (description.trim() || null) !== (feed.description || null);

  return (
    <div className="space-y-6">
      {error && (
        <p className="font-body text-sm text-destructive">{error}</p>
      )}

      <div className="space-y-3">
        <div>
          <label className="font-body text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1 block">
            Name
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={200}
            className="w-full px-3 py-2 bg-background border border-border rounded-sm font-body text-sm text-foreground"
          />
        </div>
        <div>
          <label className="font-body text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1 block">
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={2000}
            rows={3}
            className="w-full px-3 py-2 bg-background border border-border rounded-sm font-body text-sm text-foreground"
          />
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={saveDetails}
            disabled={!dirty || saving || !name.trim()}
          >
            {saving ? 'Saving…' : 'Save details'}
          </Button>
          <Button
            variant={feed.published ? 'secondary' : 'primary'}
            size="sm"
            onClick={togglePublished}
            disabled={saving}
          >
            {feed.published ? 'Unpublish' : 'Publish'}
          </Button>
        </div>
      </div>

      {feed.published && feed.feedUrl && (
        <div className="p-4 bg-muted/30 border border-border rounded-sm">
          <p className="font-body text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
            Public RSS URL
          </p>
          <div className="flex gap-2">
            <input
              type="text"
              readOnly
              value={feed.feedUrl}
              className="flex-1 px-3 py-2 bg-background border border-border rounded-sm font-mono text-xs text-muted-foreground"
            />
            <Button
              size="sm"
              variant={copied ? 'primary' : 'secondary'}
              onClick={handleCopyUrl}
            >
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
        </div>
      )}

      <div>
        <div className="flex items-baseline justify-between mb-3">
          <h4 className="font-display text-lg font-medium text-foreground">
            Episodes
          </h4>
          <span className="font-mono text-xs tracking-widest uppercase text-muted-foreground">
            {feed.items.length} item{feed.items.length === 1 ? '' : 's'}
          </span>
        </div>

        {feed.items.length === 0 ? (
          <p className="font-body text-sm text-muted-foreground py-6 text-center">
            No episodes yet. Open your{' '}
            <a href="/collection" className="underline">
              collection
            </a>{' '}
            to add conversions to this feed.
          </p>
        ) : (
          <ul className="space-y-2">
            {feed.items.map((item, index) => (
              <li
                key={item.conversionId}
                className="flex items-center gap-3 p-3 border border-border rounded-sm"
              >
                <div className="flex flex-col">
                  <button
                    type="button"
                    aria-label="Move up"
                    onClick={() => moveItem(index, -1)}
                    disabled={index === 0 || saving}
                    className="text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    ▲
                  </button>
                  <button
                    type="button"
                    aria-label="Move down"
                    onClick={() => moveItem(index, 1)}
                    disabled={index === feed.items.length - 1 || saving}
                    className="text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    ▼
                  </button>
                </div>

                <div className="flex-1 min-w-0">
                  <p className="font-body text-sm font-medium text-foreground truncate">
                    {item.title || 'Untitled episode'}
                    {!item.available && (
                      <span className="ml-2 font-mono text-xs uppercase tracking-wide text-muted-foreground">
                        (unavailable)
                      </span>
                    )}
                  </p>
                  <p className="font-mono text-xs tracking-wide text-muted-foreground">
                    {formatDuration(item.durationSeconds)} · added{' '}
                    {formatDate(item.addedAt)}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => removeItem(item.conversionId)}
                  className="font-body text-xs text-destructive hover:underline"
                  disabled={saving}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
